import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Plus, Filter, LayoutGrid, List, Grid3X3, Loader2, X, Search } from 'lucide-react';
import SpiralSpinner from '../SpiralSpinner';
import '../DeckVisualizer.css';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { ScrollArea } from '../ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Toggle } from '../ui/toggle';
import SectionHeaderShadcn from './SectionHeader.shadcn';
import { cn } from '../../lib/utils';

// Filter options - values match database field values
// Feature field determines card category (Ultra Hero, Kaiju, Scene, Ultra Mech)
const CARD_TYPE_OPTIONS = [
  { value: 'ultra_hero', label: 'Ultra Hero' },
  { value: 'kaiju', label: 'Kaiju' },
  { value: 'scene', label: 'Scene' },
  { value: 'ultra_mech', label: 'Ultra Mech' },
];

const LEVEL_OPTIONS = [
  { value: '1', label: 'Level 1' },
  { value: '2', label: 'Level 2' },
  { value: '3', label: 'Level 3' },
  { value: '4', label: 'Level 4' },
  { value: '5', label: 'Level 5' },
  { value: '6', label: 'Level 6' },
  { value: '7', label: 'Level 7' },
];

// Type field - card combat/attribute type
const TYPE_OPTIONS = [
  { value: 'basic', label: 'Basic' },
  { value: 'speed', label: 'Speed' },
  { value: 'power', label: 'Power' },
  { value: 'armed', label: 'Armed' },
  { value: 'devastation', label: 'Devastation' },
  { value: 'disaster', label: 'Hazard' },
  { value: 'meteo', label: 'Meteo' },
  { value: 'invasion', label: 'Invasion' },
];

// Rarity values match database (e.g., "900_c" for Common)
const RARITY_OPTIONS = [
  { value: '_all', label: 'All Rarities' },
  { value: '900_c', label: 'C (Common)' },
  { value: '800_u', label: 'U (Uncommon)' },
  { value: '600_r', label: 'R (Rare)' },
  { value: '500_rr', label: 'RR (Double Rare)' },
  { value: '400_rrr', label: 'RRR (Triple Rare)' },
  { value: '390_rrrr', label: 'RRRR (Quadruple Rare)' },
];

/**
 * Card Search sidebar module - shadcn/ui version
 */
function CardSearchShadcn({
  isCollapsed,
  onToggle,
  searchQuery,
  onSearchChange,
  onSearch,
  searchLoading,
  searchError,
  searchResults,
  hasMoreResults,
  onResultsScroll,
  onAddCard,
  onHoverCard,
  getProxyImageUrl,
  sidebarExpanded,
  collapseFilters,
  onToggleFilters,
  selectedCardTypes,
  onCardTypesChange,
  selectedCharacters,
  onCharactersChange,
  selectedLevels,
  onLevelsChange,
  selectedRarity,
  onRarityChange,
  onClearFilters,
  viewMode,
  onViewModeChange,
  t = (key) => null,
  hideHeader = false,
}) {
  const scrollAreaRef = useRef(null);

  // After results update, if all content fits without scrolling, auto-load more.
  // This handles large monitors where 20 results don't cause overflow.
  useEffect(() => {
    if (searchLoading || !hasMoreResults) return;
    const el = scrollAreaRef.current;
    if (!el) return;
    // Find the actual Radix ScrollArea viewport (first scrollable child)
    const viewport = el.querySelector('[data-radix-scroll-area-viewport]') || el;
    if (viewport.scrollHeight <= viewport.clientHeight) {
      onResultsScroll();
    }
  }, [searchResults, searchLoading, hasMoreResults, onResultsScroll]);

  const hasActiveFilters =
    selectedCardTypes.length > 0 ||
    selectedCharacters.length > 0 ||
    selectedLevels.length > 0 ||
    selectedRarity !== '';

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  const handleScroll = (e) => {
    // Use e.target (the actual Radix ScrollArea viewport) not e.currentTarget (the root wrapper).
    // Math.ceil(scrollTop) handles sub-pixel rounding at browser zoom levels other than 100%.
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - Math.ceil(scrollTop) - clientHeight < 100) {
      onResultsScroll();
    }
  };

  const isGridView = viewMode !== 'details';

  return (
    <div className="flex flex-col gap-2 max-w-full overflow-hidden h-full min-h-0">
      {!hideHeader && (
        <SectionHeaderShadcn
          title={t('sidebar.search.title') || 'Card Search'}
          isCollapsed={isCollapsed}
          onToggle={onToggle}
          t={t}
        />
      )}

      {!isCollapsed && (
        <div className="flex flex-col flex-1 min-h-0 gap-2">
          {/* Filter Panel */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-3 flex-shrink-0">
            {/* Search Input */}
            <div className="relative">
              <Input
                placeholder={t('sidebar.search.placeholder') || 'Search cards...'}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pr-10"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => onSearchChange('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Actions Row */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={collapseFilters ? 'outline' : 'secondary'}
                size="sm"
                onClick={onToggleFilters}
                className="gap-1"
              >
                <Filter className="h-4 w-4" />
                {t('sidebar.search.filters') || 'Filters'}
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                    {selectedCardTypes.length + selectedCharacters.length + selectedLevels.length + (selectedRarity ? 1 : 0)}
                  </Badge>
                )}
              </Button>

              {/* View Mode Toggles */}
              <div className="flex items-center border rounded-md">
                <Toggle
                  pressed={viewMode === 'details'}
                  onPressedChange={() => onViewModeChange('details')}
                  size="sm"
                  className="rounded-r-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                  title={t('sidebar.search.listView') || 'List view'}
                >
                  <List className="h-4 w-4" />
                </Toggle>
                <Toggle
                  pressed={viewMode === 'medium'}
                  onPressedChange={() => onViewModeChange('medium')}
                  size="sm"
                  className="rounded-none border-x data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                  title={t('sidebar.search.mediumGrid') || 'Medium grid'}
                >
                  <Grid3X3 className="h-4 w-4" />
                </Toggle>
                <Toggle
                  pressed={viewMode === 'large'}
                  onPressedChange={() => onViewModeChange('large')}
                  size="sm"
                  className="rounded-l-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                  title={t('sidebar.search.largeGrid') || 'Large grid'}
                >
                  <LayoutGrid className="h-4 w-4" />
                </Toggle>
              </div>

              <Button
                onClick={onSearch}
                disabled={searchLoading}
                size="sm"
                className="ml-auto gap-1"
              >
                {searchLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                {t('common.search') || 'Search'}
              </Button>
            </div>

            {/* Expandable Filters */}
            {!collapseFilters && (
              <div className="space-y-2 pt-2 border-t">
                <div className="grid grid-cols-2 gap-2">
                  {/* Card Type Filter (Feature) */}
                  <Select
                    value={selectedCardTypes[0] || '_all'}
                    onValueChange={(value) => onCardTypesChange(value && value !== '_all' ? [value] : [])}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder={t('sidebar.search.cardType') || 'Card Type'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Card Types</SelectItem>
                      {CARD_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Level Filter */}
                  <Select
                    value={selectedLevels[0] || '_all'}
                    onValueChange={(value) => onLevelsChange(value && value !== '_all' ? [value] : [])}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder={t('sidebar.search.level') || 'Level'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Levels</SelectItem>
                      {LEVEL_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Type Filter */}
                  <Select
                    value={selectedCharacters[0] || '_all'}
                    onValueChange={(value) => onCharactersChange(value && value !== '_all' ? [value] : [])}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder={t('sidebar.search.type') || 'Type'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Types</SelectItem>
                      {TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Rarity Filter */}
                  <Select value={selectedRarity || '_all'} onValueChange={(value) => onRarityChange(value === '_all' ? '' : value)}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder={t('sidebar.search.rarity') || 'Rarity'} />
                    </SelectTrigger>
                    <SelectContent>
                      {RARITY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClearFilters}
                    className="w-full text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4 mr-1" />
                    {t('sidebar.search.clearFilters') || 'Clear Filters'}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Error Display */}
          {searchError && (
            <Alert variant="destructive">
              <AlertDescription>{searchError}</AlertDescription>
            </Alert>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && (
            <ScrollArea
              ref={scrollAreaRef}
              className="flex-1 min-h-[200px] rounded-lg border bg-background"
              onScrollCapture={handleScroll}
            >
              <div
                className={cn(
                  'p-2',
                  viewMode === 'details'
                    ? 'flex flex-col gap-2'
                    : viewMode === 'large'
                    ? 'grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2'
                    : 'grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-2'
                )}
              >
                {searchResults.map((card, index) => {
                  const isScene = card.feature?.value === 'scene' || card.cardType === 'Scene';

                  return (
                    <div
                      key={index}
                      onClick={() => onAddCard(card)}
                      onMouseEnter={() => onHoverCard && onHoverCard(card)}
                      className={cn(
                        'flex cursor-pointer rounded-md bg-card transition-colors hover:bg-accent',
                        isGridView
                          ? 'flex-col items-center text-center p-2'
                          : 'items-center gap-3 p-2'
                      )}
                    >
                      <img
                        src={getProxyImageUrl(card.images?.blob?.thumbnail || card.images?.original?.thumbnail)}
                        alt={card.name}
                        className={cn(
                          'object-cover rounded',
                          isGridView
                            ? isScene ? 'w-full aspect-[88/63]' : 'w-full aspect-[63/88]'
                            : 'w-12 h-[67px]'
                        )}
                        loading="lazy"
                      />
                      {!isGridView && (
                        <>
                          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                            <span className="font-semibold truncate text-sm">
                              {card.name}{card.typeName ? ` ${card.typeName}` : ''}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {card.number}
                            </span>
                            <div className="flex gap-1 flex-wrap">
                              {card.cardType && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0">
                                  {card.cardType}
                                </Badge>
                              )}
                              {card.level && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0">
                                  Lv.{card.level}
                                </Badge>
                              )}
                              {card.characterName && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0">
                                  {card.characterName}
                                </Badge>
                              )}
                              {(typeof card.rarity === 'string' ? card.rarity : card.rarity?.description) && (
                                <Badge variant="secondary" className="text-[10px] px-1 py-0">
                                  {typeof card.rarity === 'string' ? card.rarity : card.rarity?.description}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            title={t('common.add') || 'Add'}
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddCard(card);
                            }}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })}

                {/* Loading indicator for infinite scroll */}
                {searchLoading && searchResults.length > 0 && (
                  <div className="flex items-center justify-center gap-2 py-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-muted-foreground">
                      {t('common.loading') || 'Loading more...'}
                    </span>
                  </div>
                )}


              </div>
            </ScrollArea>
          )}

          {/* Initial loading state */}
          {searchLoading && searchResults.length === 0 && (
            <SpiralSpinner size="sm" className="py-8" />
          )}

          {/* No results state */}
          {!searchLoading && searchResults.length === 0 && searchQuery && (
            <div className="text-center text-muted-foreground py-8 italic">
              {t('sidebar.search.noResults') || 'No cards found'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

CardSearchShadcn.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  searchLoading: PropTypes.bool.isRequired,
  searchError: PropTypes.string,
  searchResults: PropTypes.array.isRequired,
  hasMoreResults: PropTypes.bool.isRequired,
  onResultsScroll: PropTypes.func.isRequired,
  onAddCard: PropTypes.func.isRequired,
  getProxyImageUrl: PropTypes.func.isRequired,
  sidebarExpanded: PropTypes.bool.isRequired,
  collapseFilters: PropTypes.bool.isRequired,
  onToggleFilters: PropTypes.func.isRequired,
  selectedCardTypes: PropTypes.array.isRequired,
  onCardTypesChange: PropTypes.func.isRequired,
  selectedCharacters: PropTypes.array.isRequired,
  onCharactersChange: PropTypes.func.isRequired,
  selectedLevels: PropTypes.array.isRequired,
  onLevelsChange: PropTypes.func.isRequired,
  selectedRarity: PropTypes.string.isRequired,
  onRarityChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  viewMode: PropTypes.string.isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  t: PropTypes.func,
  hideHeader: PropTypes.bool,
};

export default CardSearchShadcn;
